import { useCallback, useEffect, useRef } from 'react';
import type { Keyboard, KeyboardKey } from '@/lib/microtonal/types';
import { frequencyForNote } from '@/lib/microtonal/scale';
import { useAudioEngine } from '@/hooks/useAudioEngine';
import { cn } from '@/lib/utils';

interface PlayableKeyboardProps {
  keyboard: Keyboard;
  /** Octave shift (adds to each key's period). */
  octaveShift?: number;
  className?: string;
}

/** Width of a white key in pixels. */
const WHITE_KEY_W = 48;
/** Width of a black key in pixels. */
const BLACK_KEY_W = 30;
/** Height of a white key. */
const WHITE_KEY_H = 160;
/** Height of a black key. */
const BLACK_KEY_H = 100;

/**
 * A playable microtonal keyboard rendered in piano style:
 * — White keys (and accent/root keys) form a full-height bottom row.
 * — Black keys are shorter and overlaid on top, positioned between white keys.
 *
 * Supports mouse, touch, and QWERTY keyboard input.
 */
export function PlayableKeyboard({ keyboard, octaveShift = 0, className }: PlayableKeyboardProps) {
  const { noteOn, noteOff, activeKeys, allNotesOff } = useAudioEngine();
  const pressedKeysRef = useRef<Set<string>>(new Set());

  const handleKeyDown = useCallback((keyId: string, freq: number) => {
    if (pressedKeysRef.current.has(keyId)) return;
    pressedKeysRef.current.add(keyId);
    noteOn(keyId, freq);
  }, [noteOn]);

  const handleKeyUp = useCallback((keyId: string) => {
    if (!pressedKeysRef.current.has(keyId)) return;
    pressedKeysRef.current.delete(keyId);
    noteOff(keyId);
  }, [noteOff]);

  // QWERTY keyboard input
  useEffect(() => {
    const keyMap = new Map<string, { id: string; freq: number }>();
    for (const key of keyboard.keys) {
      if (key.keyLabel) {
        const freq = frequencyForNote(keyboard.scale, key.degree, key.period + octaveShift);
        keyMap.set(key.keyLabel.toLowerCase(), { id: key.id, freq });
      }
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.target instanceof HTMLSelectElement) return;
      const mapped = keyMap.get(e.key.toLowerCase());
      if (mapped) {
        e.preventDefault();
        handleKeyDown(mapped.id, mapped.freq);
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.target instanceof HTMLSelectElement) return;
      const mapped = keyMap.get(e.key.toLowerCase());
      if (mapped) {
        e.preventDefault();
        handleKeyUp(mapped.id);
      }
    };

    const onBlur = () => {
      allNotesOff();
      pressedKeysRef.current.clear();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [keyboard, octaveShift, handleKeyDown, handleKeyUp, allNotesOff]);

  // Separate keys into white (bottom row) and black (overlaid top)
  const whiteKeys = keyboard.keys.filter((k) => k.color !== 'black');
  const blackKeys = keyboard.keys.filter((k) => k.color === 'black');

  // For each black key, compute which white key index it sits after.
  // We iterate through the full ordered key list and count white keys seen so far.
  const whiteKeyCountBefore = new Map<string, number>();
  let whiteCount = 0;
  for (const key of keyboard.keys) {
    if (key.color !== 'black') {
      // This is a white/accent key — it itself is a white key
      // Don't count it yet; we count how many white keys come *before* each black key
    } else {
      whiteKeyCountBefore.set(key.id, whiteCount);
    }
    if (key.color !== 'black') {
      whiteCount++;
    }
  }

  const totalWhiteKeys = whiteKeys.length;
  const totalWidth = totalWhiteKeys * WHITE_KEY_W;

  const renderWhiteKey = (key: KeyboardKey, whiteIndex: number) => {
    const isActive = activeKeys.has(key.id);
    const freq = frequencyForNote(keyboard.scale, key.degree, key.period + octaveShift);
    const isRoot = key.color === 'accent' || key.degree === 0;

    return (
      <button
        key={key.id}
        onPointerDown={(e) => {
          e.preventDefault();
          handleKeyDown(key.id, freq);
        }}
        onPointerUp={(e) => {
          e.preventDefault();
          handleKeyUp(key.id);
        }}
        onPointerLeave={() => handleKeyUp(key.id)}
        onPointerCancel={() => handleKeyUp(key.id)}
        className={cn(
          'select-none touch-none transition-colors duration-75',
          'flex flex-col items-center justify-end pb-2',
          'border border-zinc-300 dark:border-zinc-600 rounded-b-lg shadow-sm',
          'absolute top-0',
        )}
        style={{
          left: whiteIndex * WHITE_KEY_W,
          width: WHITE_KEY_W,
          height: WHITE_KEY_H,
          backgroundColor: isActive
            ? '#818cf8'
            : isRoot
              ? '#fde68a'
              : 'rgb(245 245 245)',
        }}
        aria-label={`${key.noteName ?? ''} ${freq.toFixed(1)}Hz`}
        aria-pressed={isActive}
      >
        {key.noteName && (
          <span
            className={cn(
              'text-[10px] font-medium leading-none',
              isActive ? 'text-white' : isRoot ? 'text-amber-800 dark:text-amber-900' : 'text-zinc-600 dark:text-zinc-400',
            )}
          >
            {key.noteName}
          </span>
        )}
        <span
          className={cn(
            'text-[8px] mt-1 leading-none',
            isActive ? 'text-white/80' : 'text-zinc-400 dark:text-zinc-500',
          )}
        >
          {freq.toFixed(0)}Hz
        </span>
        {key.keyLabel && (
          <span
            className={cn(
              'text-[9px] font-mono mt-0.5 leading-none',
              isActive ? 'text-white/60' : 'text-zinc-300 dark:text-zinc-600',
            )}
          >
            {key.keyLabel}
          </span>
        )}
      </button>
    );
  };

  const renderBlackKey = (key: KeyboardKey) => {
    const isActive = activeKeys.has(key.id);
    const freq = frequencyForNote(keyboard.scale, key.degree, key.period + octaveShift);
    const afterWhiteIdx = whiteKeyCountBefore.get(key.id) ?? 0;
    // Position: centered on the boundary between white key (afterWhiteIdx - 1) and white key (afterWhiteIdx)
    // afterWhiteIdx = number of white keys before this black key, so the boundary is at afterWhiteIdx * WHITE_KEY_W
    const left = afterWhiteIdx * WHITE_KEY_W - BLACK_KEY_W / 2;

    return (
      <button
        key={key.id}
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleKeyDown(key.id, freq);
        }}
        onPointerUp={(e) => {
          e.preventDefault();
          handleKeyUp(key.id);
        }}
        onPointerLeave={() => handleKeyUp(key.id)}
        onPointerCancel={() => handleKeyUp(key.id)}
        className={cn(
          'select-none touch-none transition-colors duration-75 z-10',
          'flex flex-col items-center justify-end pb-1.5',
          'rounded-b-md shadow-md border border-zinc-900 dark:border-zinc-900',
          'absolute top-0',
        )}
        style={{
          left,
          width: BLACK_KEY_W,
          height: BLACK_KEY_H,
          backgroundColor: isActive
            ? '#6366f1'
            : '#1a1a1a',
        }}
        aria-label={`${key.noteName ?? ''} ${freq.toFixed(1)}Hz`}
        aria-pressed={isActive}
      >
        {key.noteName && (
          <span
            className={cn(
              'text-[9px] font-medium leading-none',
              isActive ? 'text-white' : 'text-zinc-400',
            )}
          >
            {key.noteName}
          </span>
        )}
        {key.keyLabel && (
          <span
            className={cn(
              'text-[8px] font-mono mt-0.5 leading-none',
              isActive ? 'text-white/60' : 'text-zinc-500',
            )}
          >
            {key.keyLabel}
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      className={cn('relative overflow-x-auto', className)}
      style={{ minWidth: totalWidth }}
    >
      <div className="relative" style={{ width: totalWidth, height: WHITE_KEY_H }}>
        {/* White keys (bottom row) */}
        {whiteKeys.map((key, i) => renderWhiteKey(key, i))}

        {/* Black keys (overlaid on top) */}
        {blackKeys.map((key) => renderBlackKey(key))}
      </div>
    </div>
  );
}
